import {
    FC,
    PropsWithChildren,
    Children,
    useState,
    useEffect,
    useMemo,
    useRef,
    ReactNode
} from 'react';
import { assignInlineVars } from '@vanilla-extract/dynamic';

import { IconButton } from '@zoopla-eng/zds-components/button';
import {
    ArrowLeftMedium,
    ArrowRightMedium
} from '@zoopla-eng/zds-components/icons';

import {
    fixedWidthVar,
    totalItemsVar,
    containerStyle,
    trackStyle,
    footerContentStyle,
    footerStyle,
    navigationStyle,
    itemStyle
} from './Carousel.styles.css';

const DEFAULT_VISIBLE_ITEMS: CarouselBreakpoints = {
    S100: 1,
    S200: 2,
    M100: 3,
    L100: 3,
    L200: 4
};

const SORTED_BREAKPOINTS: [CarouselBreakpoint, number][] = [
    ['L200', 1272],
    ['L100', 1024],
    ['M100', 768],
    ['S200', 550],
    ['S100', 320]
];

const getBreakpoints = (
    overrides: Partial<CarouselBreakpoints> = {}
): CarouselBreakpoints => {
    return {
        ...DEFAULT_VISIBLE_ITEMS,
        ...overrides
    };
};

export type CarouselBreakpoints = {
    S100: 1 | 2;
    S200: 1 | 2 | 3;
    M100: 1 | 2 | 3;
    L100: 1 | 2 | 3 | 4 | 5 | 6;
    L200: 1 | 2 | 3 | 4 | 5 | 6;
};
export type CarouselBreakpoint = keyof CarouselBreakpoints;

type CarouselWithVariableWidthProps = {
    widthType: 'variable';
    visibleItemsAtBreakpoint?: Partial<CarouselBreakpoints>;
    // With "peekNextCard" set to true, if there are more cards in the carousel
    // than the current number of visible cards then this will reduce the width
    // of the cards slightly to allow the "next" card to peek through on the
    // right hand side to show the user that there are more cards. It is not
    // applied at 1280px because the full bleed effect is lost.
    peekNextCard?: boolean;
};

type CarouselWithFixedWidthProps = {
    widthType: 'fixed';
    itemWidth: number;
};

export type CarouselProps = {
    footer?: ReactNode;
    // For when the carousel is used at full width in zoopla-web, we use
    // negative margins to make it feel like the carousel bleeds off the page
    // on screen sizes where the main content is the full width of the page.
    viewportBleed?: boolean;
} & (CarouselWithVariableWidthProps | CarouselWithFixedWidthProps);

const Carousel: FC<PropsWithChildren<CarouselProps>> = (props) => {
    const { widthType, footer, viewportBleed = true, children } = props;

    const [currentIndex, setCurrentIndex] = useState(0);
    const [visibleItems, setVisibleItems] = useState(0);

    const totalItems = Children.count(children);
    const scrollEnd = totalItems - visibleItems;

    const scrollThrottleRef = useRef<ReturnType<typeof setTimeout>>();
    const containerElementRef = useRef<HTMLDivElement>(null);
    const trackElementRef = useRef<HTMLUListElement>(null);

    const itemWidth = widthType === 'fixed' ? props.itemWidth : undefined;

    // The visibleItems value is used to calculate how many items the navigation
    // should scroll through when each direction is clicked. It is derived from
    // the CSS to avoid any CLS costs associated with rendering an element and
    // _then_ checking for how many items to show.
    useEffect(() => {
        if (typeof itemWidth !== 'number') return;

        const observableElement = containerElementRef.current;

        if (!observableElement) return;

        const resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentBoxSize[0].inlineSize;

            // Ensure that the number is always an integer and always at least 1
            const visibleItems = Math.max(Math.floor(width / itemWidth), 1);
            setVisibleItems(visibleItems);
        });

        resizeObserver.observe(observableElement);

        return () => {
            resizeObserver.unobserve(observableElement);
        };
    }, [itemWidth]);

    // This has to be memoized as it is passed into the useEffect below that
    // should only update when specific props change rather than when _any_
    // props change
    const sortedBreakpoints = useMemo(() => {
        if (props.widthType !== 'variable') return;

        const breakpoints = getBreakpoints(props.visibleItemsAtBreakpoint);

        const sortedBreakpoints: { size: number; items: number }[] = [];
        for (const [breakpoint, size] of SORTED_BREAKPOINTS) {
            sortedBreakpoints.push({
                size,
                items: breakpoints[breakpoint]
            });
        }

        return sortedBreakpoints;
    }, [props]);

    useEffect(() => {
        if (!sortedBreakpoints) return;

        const resizeObserver = new ResizeObserver(() => {
            const currentBreakpoint = sortedBreakpoints.find(
                // We use window.innerWidth as it aligns with the breakpoints in
                // the CSS, inlineSize for the entry can differ if there are
                // scroll bars.
                ({ size }) => size <= window.innerWidth
            );

            // Ensure that the number is always always at least 1
            setVisibleItems(currentBreakpoint?.items || 1);
        });

        resizeObserver.observe(document.body);

        return () => {
            resizeObserver.unobserve(document.body);
        };
    }, [sortedBreakpoints]);

    const getTrackChildren = () => {
        return Array.from(trackElementRef.current?.children || []);
    };

    const handleScroll = () => {
        clearTimeout(scrollThrottleRef.current);

        scrollThrottleRef.current = setTimeout(() => {
            const children = getTrackChildren();

            if (!children.length) return;

            const firstVisibleChildIndex = children.findIndex((el) => {
                return el.getBoundingClientRect().left >= 0;
            });

            setCurrentIndex(firstVisibleChildIndex);
        }, 500);
    };

    const handleNavigation = (direction: 'prev' | 'next') => {
        const newIndex =
            direction === 'prev'
                ? Math.max(currentIndex - visibleItems, 0)
                : Math.min(currentIndex + visibleItems, scrollEnd);

        const child = getTrackChildren()[newIndex];

        if (child) {
            child.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start'
            });

            // The child.scrollIntoView above will set this currentIndex value
            // itself, but the throttling means that there is a small lag so
            // to avoid this we set it here immediately so that the buttons
            // update instantly.
            setCurrentIndex(newIndex);
        }
    };

    // TODO: Memoize this and add an explanation for it
    const getNavigationVariants = ():
        | Record<CarouselBreakpoint, boolean>
        | undefined => {
        if (widthType !== 'variable') return;

        const hideNavigationVariants: Record<CarouselBreakpoint, boolean> = {
            S100: false,
            S200: false,
            M100: false,
            L100: false,
            L200: false
        };

        const breakpoints = getBreakpoints(props.visibleItemsAtBreakpoint);
        for (const [breakpoint] of SORTED_BREAKPOINTS) {
            hideNavigationVariants[breakpoint] =
                breakpoints[breakpoint] >= totalItems;
        }

        return hideNavigationVariants;
    };

    const navigationVariants = getNavigationVariants();

    return (
        <div>
            <div
                className={containerStyle({
                    widthType,
                    viewportBleed,
                    ...(widthType === 'variable' && {
                        ...getBreakpoints(props.visibleItemsAtBreakpoint),
                        peekNextCard: props.peekNextCard
                    })
                })}
                style={assignInlineVars({
                    [totalItemsVar]: totalItems.toString(),
                    [fixedWidthVar]: itemWidth
                        ? `${itemWidth.toString()}px`
                        : ''
                })}
                tabIndex={0}
                onScroll={handleScroll}
                ref={containerElementRef}
            >
                <ul
                    className={trackStyle({ widthType })}
                    ref={trackElementRef}
                    // This is a legacy test id from the previous carousel.
                    // Please use best practice queries when using this in your
                    // tests as this will be refactored out in due course.
                    data-testid="carousel"
                >
                    {Children.map(children, (child, index) => (
                        <li className={itemStyle} key={index}>
                            {child}
                        </li>
                    ))}
                </ul>
            </div>

            <div className={footerStyle}>
                {footer && <div className={footerContentStyle}>{footer}</div>}

                <div
                    className={navigationStyle({
                        ...(widthType === 'variable' && {
                            hideOnDefault: totalItems === 1,
                            ...(navigationVariants || {})
                        })
                    })}
                >
                    <IconButton
                        category="secondary"
                        label="Previous slide"
                        onClick={() => {
                            handleNavigation('prev');
                        }}
                        dangerouslyDisabled={
                            currentIndex === 0 || totalItems === 1
                        }
                    >
                        {ArrowLeftMedium}
                    </IconButton>
                    <IconButton
                        category="secondary"
                        label="Next slide"
                        onClick={() => {
                            handleNavigation('next');
                        }}
                        dangerouslyDisabled={
                            currentIndex >= scrollEnd || totalItems === 1
                        }
                    >
                        {ArrowRightMedium}
                    </IconButton>
                </div>
            </div>
        </div>
    );
};

export default Carousel;
